import React from 'react';
import { Image } from '../Image/Image';
import './Logo.scss';

/**
 * Logo - Lightweight logo component for MegaMenu navigation
 * 
 * Extends native <img> element with CMS convenience features:
 * - `imageUrl` - CMS-friendly alias for src attribute
 * - `transparentImageUrl` - Optional transparent logo variant for sticky nav
 * - `url` - Link wrapper URL for logo click navigation
 * - `altText` - CMS-friendly alias for alt attribute
 * - `ctaId` - Analytics tracking ID (data-cta-id attribute)
 * - All other HTML img attributes pass through unchanged
 * 
 * @example
 * ```tsx
 * // Basic logo with CMS props
 * <Logo imageUrl="https://cdn.example.com/logo.svg" altText="Brand Name" />
 * 
 * // Logo with link wrapper and analytics
 * <Logo 
 *   imageUrl="https://cdn.example.com/logo.svg" 
 *   altText="Brand Name"
 *   url="/"
 *   ctaId="header-logo"
 * />
 * 
 * // Logo with transparent variant for sticky nav
 * <Logo 
 *   imageUrl="https://cdn.example.com/logo.svg"
 *   transparentImageUrl="https://cdn.example.com/logo-transparent.svg"
 *   altText="Brand Name"
 * />
 * ```
 */

/**
 * @polySemanticUsage Placeholder for now
 */
export interface LogoProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  /**
   * Image source URL (CDN or local path)
   * CMS convenience prop - alias for native 'src' attribute
   */
  imageUrl?: string;
  
  /**
   * Optional transparent background logo variant
   * Used when navigation becomes sticky or on transparent backgrounds
   */
  transparentImageUrl?: string;
  
  /**
   * Link URL for logo click navigation (typically homepage)
   * When provided, wraps logo in an anchor tag
   */
  url?: string;
  
  /**
   * Alt text for accessibility
   * CMS convenience prop - alias for native 'alt' attribute
   */
  altText?: string;
  
  /**
   * Analytics tracking ID
   * Rendered as data-cta-id attribute for click tracking
   */
  ctaId?: string;
  
  /**
   * Whether to use the transparent logo variant
   * Typically controlled by parent component based on sticky nav state
   */
  useTransparent?: boolean;
}

export const Logo: React.FC<LogoProps> = ({
  imageUrl,
  transparentImageUrl,
  url,
  altText,
  ctaId,
  useTransparent = false,
  className = '',
  ...imgProps
}) => {
  // Use transparent variant if available and useTransparent is true
  const finalImageSrc = useTransparent && transparentImageUrl ? transparentImageUrl : (imageUrl || '');
  
  // Determine alt text
  const imageAlt = altText || '';
  
  const logoImage = (
    <Image
      url={finalImageSrc}
      alt={imageAlt}
      bare
      className={`logo-image ${className}`}
      loading="lazy"
      role={imageAlt ? 'img' : 'presentation'}
      aria-label={imageAlt || undefined}
      aria-hidden={!imageAlt ? 'true' : undefined}
      {...imgProps}
    />
  );

  // If url is provided, wrap in link
  if (url) {
    return (
      <div className="logo-wrapper">
        <a 
          href={url}
          data-cta-id={ctaId}
          aria-label={imageAlt || 'Home'}
        >
          {logoImage}
        </a>
      </div>
    );
  }

  return (
    <div className="logo-wrapper">
      {logoImage}
    </div>
  );
};
